import type { IntlShape } from 'react-intl-next';
import type { AnyEventObject } from 'xstate';
import { actions, assign, createMachine } from 'xstate';

import { type JSONDocNode } from '@atlaskit/editor-json-transformer';
import { type Fragment, type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { type MentionNameDetails } from '@atlaskit/mention';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';
import type { ContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import type { MultiPromptType } from '@atlassian/editor-ai-common/src/utils/multiPrompts';

import type {
	AnalyticsContext,
	EditorPluginAIConfigItemMarkdown,
} from '../config-items/config-items';
import type { ConvoAIResponseRovoAction, EditorPluginAIProvider, RovoActions } from '../types';
import { type EndExperience } from '../types';
import { buildChannelId } from '../utils/agents/build-channel-id';

import { streamingService, streamingServiceAIButton } from './streaming-service';

/**
 * This type models when there is a "generate prompt" being used.
 *
 * This happens either at the start of the ai experience, _or_ when the  user
 * interrogates the prompt.
 */
type LatestGeneratePrompt = {
	presetTitle?: never;
	userInput: string;
	userADFInput?: JSONDocNode;
};
/**
 * This type models when there is a non-generative preset prompt being used.
 * @example
 * Example of Improve writing:
 * ```
 * {
 *   // config item used for latest prompt
 *   presetTitle: EditorPluginAIConfigItemMarkdown,
 * }
 * ```
 * @example
 * Example of Brainstorm:
 * ```
 * {
 *   // config item used for latest prompt
 *   presetTitle: EditorPluginAIConfigItemMarkdown,
 *   userInput: 'Facts about cats',
 * }
 * ```
 */
type LatestPresetPrompt = {
	presetTitle: EditorPluginAIConfigItemMarkdown;
	userInput?: string;
	userADFInput?: JSONDocNode;
};

export type LatestPrompt = LatestGeneratePrompt | LatestPresetPrompt;

export type PromptTrigger = 'interrogate' | 'generate' | 'custom';

export type ExperienceMachineOptions = {
	context: AIExperienceMachineContext;
	endExperience: EndExperience;
};

export type HistoryEvent = {
	userInput: string;
	userADFInput?: JSONDocNode;
	configItem: EditorPluginAIConfigItemMarkdown;
	markdown: string;
	aupViolation?: boolean;
	responseTooSimilar?: boolean;
	cacheKey: string;
	modelInput: ModelInput;
	inputOutputDiffRatio?: string;
	inputContentStatistics?: ContentStatistics;
	multiPrompts?: MultiPromptType[];
};

export type ContextStatistics = {
	inputDocument: ContentStatistics;
};

export type AIExperienceMachineContext = {
	aiProvider: EditorPluginAIProvider;
	intl: IntlShape;
	positions: [number, number];
	editorView: EditorView;
	// This is added so;
	// - the kick state guard can pick if the config item is the base generate
	// Note -- currently only the empty base generate is used (IC4 adds a
	// base generate for range selections)
	baseGenerate?: EditorPluginAIConfigItemMarkdown;
	configItem: EditorPluginAIConfigItemMarkdown;
	/**
	 * This is input that the user types into either
	 * - the base generate prompt
	 * - or a "custom prompt" (ie. brainstorm)
	 */
	/**
	 * The userInput is intended to contain the latest contents of the user input field on
	 * the prompt input screens, and the refinement screen.
	 */
	userInput?: string;
	/**
	 * When editor is used as prompt input, we will store ADF along side userInput.
	 */
	userADFInput?: JSONDocNode;
	/**
	 * This is expected to be set when transitioning to the review state.
	 * It indicates that the user has attempted to use the AI feature with
	 * an input or content which violates Atlassian's Acceptable Use Policy.
	 *
	 * It's cleared when the user re-enters the loading state.
	 */
	aupViolation: boolean;
	markdown?: string;
	//TODO: multiPrompts experiment cleanup - editor_ai_-_multi_prompts
	multiPrompts?: MultiPromptType[];
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	/**
	 *
	 */
	latestPrompt?: LatestPrompt;
	editCount: number;
	/**
	 * Iniitial sketching of handling `latestPromptResponse` being passed/handled
	 * at various areas, leave in context for now
	 */
	latestPromptResponse: string | null;
	/**
	 * Used to track what/where the prompt came from. promptTrigger
	 * is mapped in analytic events to promptType (though promptType
	 * is sometimes manually evaluated in other events e.g. prompt userFlow).
	 */
	promptTrigger?: PromptTrigger;
	latestPromptRetried: boolean;
	retryPromptCount: number;
	failureReason?:
		| 'unknown fail'
		| 'token limit fail'
		| 'insert failure'
		| 'input too short fail'
		| 'hipaa content fail';

	/**
	 * Only using this for analytics which is why we delete this from context
	 * after we use it.
	 */
	apiErrorInfo: {
		errorKey?: string;
		statusCode?: number;
		errorContent?: string;
		apiName?: string;
	};
	/**
	 * This is set when converting prosemirror to markdown, and consumed when converting markdown received from the API back to prosemirror.
	 *
	 * It won't be used by all prompts, and can be undefined.
	 *
	 * Currently this is opinionated about only one thing being sent to the llm having an idMap (ie. if a full document is sent -- it will be only be converted to markdown)
	 */
	idMap?: IDMap;
	selectionType?: 'range' | 'empty';
	/**
	 * This is used to support returning to the previous response when the user resubmits the same input to prevent additional API calls.
	 * Interrogate and retry prompt make fresh API requests and do not read from this.
	 */
	responseHistory: {
		/**
		 * This property is currently unused but is intended to be used to
		 * traverse the history object array in future as we build out this feature.
		 */
		positionFromEnd: number;
		entries: HistoryEvent[];
	};
	/**
	 * This stores stats such as nodeCounts, markCounts etc of context contents sent to the BE
	 * such as the context document (and in future, also the prompt user input context)
	 */
	contextStatistics?: ContextStatistics;
	/**
	 * This stores the loading status that will be used to update the "Generating" message on the
	 * Loading Screen.
	 */
	loadingStatus?: string;

	/**
	 * The channelId for a stateful assistance-service chat session.
	 */
	channelId?: string;

	rovoActions?: RovoActions;

	previousAgentId?: string;

	/**
	 * Helper function to get the mention name details for a given mention id.
	 * We can no longer get the resolved mention name from the node attributes
	 * so we need to fetch it from the mention service.
	 */
	getMentionNameDetails?: (id: string) => Promise<MentionNameDetails | undefined>;

	/**
	 * function to call when Discard is confirmed
	 * which will be reset by "disable discard" before being called
	 */
	onceDiscard?: () => unknown;

	/** The analytics context for the current user AI Experience journey. */
	analyticsContext: AnalyticsContext;

	//TODO: AI Button experiment cleanup - platform_editor_ai_ai_button_block_elements
	triggeredFor?: NodeType;
	//TODO: AI Button experiment cleanup - platform_editor_ai_ai_button_block_elements
	fragment?: Fragment;
};

/**
 * This is a state machine for the AI Experience. Consider this the source
 * of truth for the AI modal the moment it appears to the moment it closes.
 *
 * The state is initialised in ExperienceApplicationStateWrapper via
 * useAIExperienceService().
 *
 * The state machine can be updated from React components via send() or
 * streamingService() as the generated response is streamed back from endpoint.
 *
 * Use the XState extension to access a visual editor for the state machine.
 * When making changes to the state machine, please update the visualisation
 * and add a screenshot of the new layout in your PR.
 *
 * @see https://hello.atlassian.net/wiki/spaces/EDAI/pages/3222977194/HOWTO+-+Using+XState+state+machine+in+Editor+AI#How-do-I-update-the-%40xstate-layout-diagram-in-code%3F
 */
export function createAIExperienceMachine(options: ExperienceMachineOptions) {
	// @ts-ignore types are not up to date, to be fixed with xstate update to v5
	const machine = createMachine(
		{
			/** @xstate-layout N4IgpgJg5mDOIC5QEECSACAogDwA5gCcBLMAOwGMx0BZAQ3IAsjSwBiAV1wloBcrcA9rCI8iA0rADaABgC6iUIOGjxCkNkQBGAMwA2ABwA6abukB2TZv37tAVgBMmgCwAaEAE8t2w-oCcm+207bXtA-199AF9ItzQsPEISCio6RmYwQ1geWgI+CHQs3jYAayJyYvQBADMqmXkkECURMVI1DQRNM0cfWxsQ7U1bXVsBt08EEOljMzNbX10nW1mh+ydo2IwcfGIyShp6JhZM7NzIAuy+VlLyyprJTXrFIWbVBvbLeyn7Je1pc1NpJYxohJsZ9E5fNJwU5Zk5dNp1iA4ltErsUgd0oYALYCbgAG3Q9FEADcqIVLnByLR8HU1E0VK03oh7L4LIZnGYbPpHE4WfYzMCEL1fIZTCM5mYQty4YjkQkdsl9mkjjj8YTyCSyRc2FkBAQwABhcR8bA8ADKFyIWTKUjkdOeDLaIPsunshhCFjMul8TmkoRmgpdIrhei6phZcN8ss28qSe1ShwyqtoBKJRFJ5yKHC4RUJMFIPFtj0aDpaToQLMB7K9cOk2iccICge9hhD3u0kIc+jmaxiSJj2zj6OVSdxKfVmszfEMYFjaMMeIEtAgzCgU7Y5AEWNweLAfFpDXpZaZHV0XXdzgbZ6G4N82kFASh7pm-P0XQCQQRfblg7RSsT2JjqmGrplqRQznOyQLkuK6kGu5JsOwpDFKQAgAO6kOgVS0EQeIHk8yjHqA7SrMMhhepCThvjCLq2A+vy2D4DYRJ8VFeqs0bxL+ioJpiybAZOCEQdxlDQcuq7rqwsC0FiVBUowVDFGA7j4SWhGvMRXh2KKzjXiYHp6IKb6aIYthONokpnto3JhpxKIKvGGIqkBE6geuwmolBi7iXB7nebBUBSTw+oyapR4aeoWjgt4LoQqEQzer4-JGb4IpWAYUrUb6X4bFxnmOSOgFqmmGZCbOIkZP5ElCVVcHZtwfDoIwtCkCweKoBAYWlhF7zzCKUL6Jo5i2FYfqSoKVnkfW5hvt60hDDl-Z5Q5w4AfxrmldqHkrWJAV+TBq71bmRAQHQuBdepjKaRWYQ+AN9jgn6gKckZZhOMYDjSD6VhJSNi0-vlq18S5JVgdO5WA7t1VbbVgXUkQWE4XhdqHt1V2RRWQSMYNb6Qh2SV3g+UImboBiQgTrodnZkEFWtIMgZt4EQztsP7T5gUELmeJEFiIiQBdLzoyRTiaLohguvouiWBZwzsSlRhDPMnwmA2MK6NTFX-sDxUM2DGTM0OUO+TVB11TwAhKZh3O8zwiO4QLjonqEVYi-yb2SssvgPqRrZnpYPqct8ksa4DWvOTrglbQb86syb7OsMwuDsLb5sCAUDB6rb2H2yjBGC+Wnz+KKrrmZKA1wg+vJi+Tvgjd8IyDCHK1h6OEduWVNOVab8Ew93rBMLgtC0E1xpkFnSMO0RGNhGLb4mMTwwNvYD6aJChj+H4X0spTUbfgOoe8eH46g+50ded3bMBawyAAKoAAroMSYh4rwLToBAe5gBq-O52p+cnqvKid1vSdF5CGRYK9zyDG7INWw5hPhmCbkOFuRVj661Pp3I2PdwKw1YPqWAggJBUFTgUHmuEciTx6lFZw4teS-Asg4RYy8PBeBbPNKWiwQGb3sEgv8h9W5oMjkzTBsctrwztrufICFWDiP1MFFSv9wpC2ZJ9VsgcFidFASYImQRRSzF+tyEYoR-r72bvw1BAl25RxERfISnNGrWxEBtNg2AhK0CqHwAgAAKAASpgAAKj4gAmgAfWQAAGXCQAeQAOqYAACIAEpr6mOQeY9aJ8O4VSwe5exVBHG21BpQ5RFZXRTFdLCBYI0FjmCMqYdeZ4vrsVZBEXeuV7JpKcgIyxjNwY2PZrkrmZDbbMFBjI3ACM5EEAUcWJRBcvqMXmhZMwfoDDemYeMN6wYAgskls4Pwg1eE8S6RY5xGDsn6ifmANCklmCeIIAIKARRinlg5EYQEtd4Si1sL0UYLCOh1m8A4EIqVFhfX8Ec2m2tBFWOERcsAVybnSKQihdCmFs7I1mWjAucJ3oL1+SNPwugHwjCMIEfQX0IS9Elo4SFQMj49L1ttQ2lySBIu1Hgvc0z0C4AeduHgLynZUTdJ8VeKsqILIfG9MW3xpB0PMsMQY6s97LU6YVDJ6CsmQ1ZdcySkAnG8q3LgAVijsUnhGiZbsKyYSpTMr8L2-yEo+ApWeB6NEAi2DpSgjVQi+nwsRe5XlCK2VBRyLbfUVR0iyQLIK66Sq3T2tAaTOYHqHykzdCs1pP0-htKWh0vhJyfWwr9dq4Nuq7FgEjSwaNPBWCUmpGAWNGMlUimlJyeEb16yaAfMZd0DZrBK1CL6T1Kr83HPVfTX1+tME6vZeBCNUax6sCpMkAkC7q1jybe8H5jEQGsmcF9VZUrfjkW9MKeEvpRaINHZ3b1k7i3Tv9Wy3JlbF0FiOo1dgsBCDoETsnLdUUIhMTfBSn5MwDBprmOvP6z0paryiDezW6T729MfaWgNFaq1gBrR+qgX6f1-sKRAWoprLrzJFsXT5kohidDelKgwpkIwui9P7BD7Tb3Ibbqh5l85NxYixC1fIg9dw8EatI64xRqhVHQGhEQDAR78cEzylMe5GqEYAx0LGaiOwHLMKyCD-zErsh+R2GYNgQyaC9ZxmF3Gz6iT4wJ0gQmVOib1rhkeBZZwmqxWRk88IRX+09IS0w95-ktNFPNMBFL5gQjY3mjjhaUNMrsxkBzSnhOqbcyuygmL7S+bjQNRjnJ-C9F5KsImNDuykoiKNIIuaAZmMS1x5LmC0tOeUyJsTHLstgDwg8PL-8406EYj8nQo0-R3gcIGJpopHAfBFnoUIVmms2aZSuWAVICAQEMKM3WdbSC0AAEa7nflaTbnVSODYxiEb4PhHyuwbAlabrJZuWACAt10PDEMHxW4y9y63zuGDGeto7J2Ac5Auz5q77QBjzXZP6QY8xnBJQfHebwUIzyLA7A2AI172NId+2coS4OttA72-Wmkl3HbXQGLoq1OgIgGF9PoabLoLy7LMo4Osu8+yoQ-vABoDW1WJgG9T67Z4ejWr07XcyX0e0mXmHoH5qzaezGW4VQopwpHalF1PEiuzjABFMGUmwQxXD-IGCKWYD1TC+kcJKOLQuC0Tua+uXXVCEAi2xksOV0u7Vy4t9ZcWXbWIPSsDLdXdNXdapWu7kpIZJe+9tbLh14xeSMUlCLIaehggQu+41l3q3zmQ1EUUOPry9OMViklb4pNUrJUdToYPvpbUWUi5H6Ff2Y+G1L9OWG5enbDfZJjmw3yl6CkZ+RPTdZBq+ldNyDvDKifWOyb3jI4iMVnAQgP669cRVUR9FLQOoXxhDW5ORawXYatmRGov7py+4Ul9sVtPJ6ACnOJ39PC9d3vSShr2VifFkUUO8MUEuCwbsO-U5TJFfJ-AZOxIZG2X9A7XWT-EicMVsL6fZZwP6DZRAL0KYCEOKBYW3bsSzfPYXTvB-EtHaWdN3VGfLZtesDPX4VeMpYYaiElc8RYMbKwIOKuSAotWzGdMtOdacINRFVArwO8KaD5Ng3oOjf5ToepWYXSbsGpV1AQpLYvGgkQl9LDGtSQjoaLd0BYXGNQ2jc3U-L0MwcWf0O8CwZNPHeLAnQvLvGAnaTfCAQw1YaQyUayCEQYPTeEOiRQ8ybwbPD1fweBEdfHH7VwqgtDHaNrZzTrPWQwroGw34DKUmefIYAUQzUmZ1PoPwBwM8PTTQ6PLaEnLw+g6HZkWuIwH0PZXSHwoIHtRYUySWZYRYTsSUCoovYnM7CHHbZAzUQwqURZXkO8XHLeUWabVecWdZEYJKOEF0fotw8CaosnMY2osXGHP4auKiODVYiENo-5UVEUIMZHLIuEXoaIaIIAA */
			id: 'AI Experience Machine',

			initial: 'started state',

			states: {
				'started state': {
					on: {
						'kick off': [
							{
								target: 'modal active state.experience.loading state',
								cond: 'neither base generate nor custom prompt',
							},
							'#AI Experience Machine.modal active state.experience.command palette state',
						],
					},
				},

				'modal active state': {
					type: 'parallel',

					on: {
						escape: {
							target: '#AI Experience Machine.modal active state.discard.active',
						},
						storeContextStatistics: {
							actions: 'storeContextStatistics',
						},
					},

					states: {
						experience: {
							states: {
								'loading state': {
									on: {
										complete: {
											target: 'review state',
											actions: [
												'pushToHistory',
												'updateMarkdown',
												'clearUserInput',
												'updateRovoActions',
											],
										},
										'unknown fail': {
											target: 'failed',
											actions: 'fail',
										},

										'same cache key': {
											target: 'review state',
											actions: ['restoreResponseFromHistory', 'clearUserInput'],
										},
									},

									states: {
										loading: {
											invoke: {
												//TODO: AI Button experiment cleanup - platform_editor_ai_ai_button_block_elements
												src: editorExperiment('platform_editor_ai_ai_button_block_elements', 'test')
													? streamingServiceAIButton
													: streamingService,
											},
											on: {
												stream: {
													target: 'loading',
													internal: true,
													actions: ['updateMarkdown', 'updateLoadingStatus'],
												},

												'handle additional prompts': {
													target: 'loading',
													internal: true,
													actions: ['updateAdditionalPrompts'],
												},

												'update idMap': {
													target: 'loading',
													internal: true,
													actions: 'updateIdMap',
												},

												'api fail': {
													target: 'api failed state',
													actions: 'storeFailureContext',
												},
												'rate limited': {
													target: 'rate limit active',
													actions: 'storeFailureContext',
												},

												'token limit fail': {
													target: '#AI Experience Machine.modal active state.experience.failed',
													actions: 'fail',
												},

												'input too short fail': {
													target: '#AI Experience Machine.modal active state.experience.failed',
													actions: 'fail',
												},

												'hipaa content fail': {
													target: '#AI Experience Machine.modal active state.experience.failed',
													actions: 'fail',
												},

												'AUP violation detected': {
													target:
														'#AI Experience Machine.modal active state.experience.review state',

													actions: [
														'pushToHistory',
														'storeAupViolation',
														'clearUserInput',
														'storeFailureContext',
													],
												},
												'response too similar': {
													target:
														'#AI Experience Machine.modal active state.experience.review state',

													actions: ['pushToHistory', 'clearUserInput'],
												},
											},
										},

										'api failed state': {
											on: {
												'api retry': 'loading',
											},

											exit: ['clearFailureContext', 'clearLoadingStatus'],
										},

										'rate limit active': {
											after: {
												RETRY_ALLOWED: { target: 'rate limit inactive' },
											},
										},

										'rate limit inactive': {
											on: {
												'api retry': {
													target: 'loading',
													actions: 'clearLoadingStatus',
												},
											},

											exit: ['clearFailureContext'],
										},
									},

									initial: 'loading',
									entry: ['setLatestPrompt', 'updateChannelId'],
									exit: 'clearLoadingStatus',
								},

								'review state': {
									on: {
										'handle additional prompts': {
											target: 'review state',
											actions: ['updateAdditionalPrompts'],
										},
										interrogate: {
											target: 'loading state',
											actions: [
												'clearFromHistory',
												'updatePromptContextBasedOnTrigger',
												'interrogate',
											],
										},
										'unknown fail': {
											target: 'failed',
											actions: 'fail',
										},
										'retry prompt': {
											target: 'loading state',
											actions: [
												'updatePromptContextBasedOnTrigger',
												'updateUserInput',
												'updateUserInputADF',
												'clearMarkdown',
												'incrementRetryPromptCount',
												'clearFromHistory',
											],
										},
										'edit prompt': {
											target: 'command palette state',
											actions: ['editPrompt'],
										},
									},

									states: {
										preview: {
											on: {
												'start refinement': 'refinement',
											},
										},

										refinement: {
											on: {
												escape: {
													target: '#AI Experience Machine.modal active state.discard.active',
													actions: ['clearUserInput', 'clearFocus'],
												},
												'cancel refinement': {
													target: 'preview',
													actions: ['clearUserInput', 'clearFocus'],
												},
												'update user input': {
													internal: true,
													actions: 'updateUserInput',
												},
												'update user input adf': {
													internal: true,
													actions: 'updateUserInputADF',
												},
											},
										},
									},

									initial: 'preview',
								},

								failed: {},

								'command palette state': {
									on: {
										'kickoff with command palette input': {
											target: 'loading state',
											actions: ['updatePromptContextBasedOnTrigger', 'updateContext'],
										} as any,

										'update context': {
											target: 'command palette state',
											internal: true,
											actions: ['updateContext'],
											// ! TODO: When xstate v5+ is adopted remove the ts-expect-error
											// This will swallow ts-errors, take care
											// This occurs because createMachine's options input does not include state.description
											// However the state.description field is useful for the visual editor
											// Tested 2023-07-06 on xstate@5.0.0-beta.15 and this problem will be resolved
											description: `This must be called before 'cancel' transition to make sure the context is updated before the 'if command input is empty' guard is evaluated.`,
										},

										cancel: [
											{
												cond: 'if command input is empty',
												target: 'command palette state',
												internal: true,
												actions: 'endExperience',
											},
											'#AI Experience Machine.modal active state.discard.active',
										],
									},

									description: `This state creates a nested state machine that handles all the state of the command palette.

  It needs to know the 'userInput', 'baseGenerate' and 'configItem' from the AI Experience State Machine.

  Whenever we exit this state we need to update the AI Experience State Machine's context with the 'updateContext' action.`,
								} as any,
							},
						},

						discard: {
							initial: 'inactive',

							states: {
								inactive: {
									on: {
										'enable discard': {
											target: 'active',
											actions: 'updateContext',
										},
									},
								},
								active: {
									on: {
										'disable discard': {
											target: 'inactive',
											actions: 'updateContext',
										},
										escape: 'inactive',
									},
								},
							},
						},
					},
				},
			},

			schema: {
				// Typed explicitly due to old xstate version not having
				// type generation
				events: {} as AIExperienceMachineEvent,
			},

			context: options.context,

			on: {
				'update positions': {
					internal: true,
					actions: 'updatePositions',
				},
			},
		},
		{
			actions: {
				updatePositions: assign((context, event) => {
					if (event.type !== 'update positions') {
						return {};
					}
					return { positions: event.positions };
				}),

				storeFailureContext: assign((context, event) => {
					if (
						['api fail', 'rate limited', 'AUP violation detected'].includes(event.type) &&
						'errorInfo' in event
					) {
						return { apiErrorInfo: event.errorInfo };
					}
					return {};
				}),

				clearFailureContext: assign(() => {
					return { apiErrorInfo: {} };
				}),

				updateMarkdown: assign((context, event) => {
					if (event.type !== 'stream' && event.type !== 'complete') {
						return {};
					}

					return {
						markdown: event.markdown,
						//TODO: AI Button experiment cleanup - platform_editor_ai_ai_button_block_elements
						fragment: event.fragment,
					};
				}),

				//TODO: multiPrompts experiment cleanup - editor_ai_-_multi_prompts
				updateAdditionalPrompts: assign((context, event) => {
					if (event.type !== 'stream' && event.type !== 'handle additional prompts') {
						return {};
					}

					// With the current implementation, there should always be a response in history
					// before we add multiPrompts to context. This is because we only add multiPrompts
					// after a response is generated and multiprompts are additional API calls after.

					const responseInHistory = context.responseHistory.entries[0];
					const updatedHistoryEvent = { ...responseInHistory, multiPrompts: event.multiPrompts };

					return {
						...(responseInHistory && {
							responseHistory: {
								entries: [updatedHistoryEvent],
								positionFromEnd: 0,
							},
						}),
						multiPrompts: event.multiPrompts,
					};
				}),

				updateLoadingStatus: assign((context, event) => {
					if (event.type !== 'stream') {
						return {};
					}
					// If the loading status is empty, we want to clear it from the context
					if (event.loadingStatus === '') {
						return { loadingStatus: undefined };
					}
					return {
						loadingStatus: event.loadingStatus,
					};
				}),

				// @ts-ignore types are not up to date, to be fixed with xstate update to v5
				clearLoadingStatus: assign(() => {
					return { loadingStatus: undefined };
				}),

				storeContextStatistics: assign((_context, event: AIExperienceMachineEvent) => {
					if (event.type === 'storeContextStatistics') {
						return {
							contextStatistics: event.contextStatistics,
						};
					}
					return {};
				}),

				storeAupViolation: assign((context, event) => {
					if (event.type === 'AUP violation detected') {
						return {
							aupViolation: true,
							// clear any markdown that may have already come through
							// at time of implementation -- this is not possible -- as aup check happens before
							// streaming
							markdown: '',
						};
					}
					return { aupViolation: true };
				}),
				clearMarkdown: assign((context, event) => {
					if (event.type !== 'retry prompt') {
						return {};
					}
					return { markdown: undefined, idMap: undefined };
				}),

				interrogate: assign((_context, event: AIExperienceMachineEvent) => {
					if (event.type !== 'interrogate') {
						return {};
					}
					/**
					 * Need to wipe out markdown as per user story designs where we don't use
					 * the prior response in the latest response
					 */
					return {
						markdown: '',
						userInput: event.userInput,
						userADFInput: event.userADFInput,
						latestPromptResponse: event.latestPromptResponse,
					};
				}),

				clearUserInput: assign((context, event) => {
					return { userInput: '', userADFInput: undefined };
				}),

				updateUserInput: assign((context, event) => {
					if (event.type !== 'update user input' && event.type !== 'retry prompt') {
						return {};
					}
					return { userInput: event.input };
				}),

				updateUserInputADF: assign((context, event) => {
					if (event.type !== 'update user input adf' && event.type !== 'retry prompt') {
						return {};
					}
					return { userADFInput: event.adf };
				}),

				updateRovoActions: assign((context, event) => {
					if (event.type !== 'complete') {
						return {};
					}

					// We don't want to update if there are no rovo actions or
					// an agent hasn't been set
					if (!event.rovoActions?.length || !context.configItem.agent) {
						return { rovoActions: {} };
					}

					return {
						rovoActions: event.rovoActions.reduce<RovoActions>((prevValue, currentValue) => {
							const key = currentValue.data.suggestion === 'insert' ? 'empty' : 'range';
							prevValue[key] = currentValue.data;
							return prevValue;
						}, {}),
					};
				}),

				pushToHistory: assign((context, event) => {
					if (
						event.type !== 'complete' &&
						event.type !== 'AUP violation detected' &&
						event.type !== 'response too similar'
					) {
						return {};
					}

					const { cacheKey } = event;

					// Right now we only ever have one object in history as we do not
					// yet support traversing the history object array. This is also to
					// prevent us saving large documents into history multip[le times.
					if (
						context.responseHistory.entries.length > 0 &&
						context.responseHistory.entries[0].cacheKey === cacheKey
					) {
						return {};
					}

					if (event.type === 'complete') {
						const historyEvent: HistoryEvent = {
							userInput: context.userInput || '',
							userADFInput: context.userADFInput,
							configItem: context.configItem,
							markdown: event.markdown,
							cacheKey,
							modelInput: event.modelInput,
							inputOutputDiffRatio: event.inputOutputDiffRatio,
						};
						return {
							responseHistory: {
								entries: [historyEvent],
								positionFromEnd: 0,
							},
						};
					}

					if (event.type === 'AUP violation detected') {
						const historyEvent: HistoryEvent = {
							userInput: context.userInput || '',
							userADFInput: context.userADFInput,
							configItem: context.configItem,
							markdown: '',
							aupViolation: true,
							cacheKey,
							modelInput: event.modelInput,
						};
						return {
							responseHistory: {
								entries: [historyEvent],
								positionFromEnd: 0,
							},
						};
					}

					if (event.type === 'response too similar') {
						const historyEvent: HistoryEvent = {
							userInput: context.userInput || '',
							userADFInput: context.userADFInput,
							configItem: context.configItem,
							markdown: '',
							responseTooSimilar: true,
							cacheKey,
							modelInput: event.modelInput,
							inputOutputDiffRatio: event.inputOutputDiffRatio,
						};
						return {
							responseHistory: {
								entries: [historyEvent],
								positionFromEnd: 0,
							},
						};
					}

					return {};
				}),

				restoreResponseFromHistory: assign((context, event) => {
					if (event.type !== 'same cache key') {
						return {};
					}

					const { entries } = context.responseHistory;
					const historyEvent = entries[0];

					if (!historyEvent) {
						return {};
					}

					return {
						userInput: historyEvent.userInput,
						userADFInput: historyEvent.userADFInput,
						markdown: historyEvent.markdown,
						configItem: historyEvent.configItem,
						latestPromptResponse: historyEvent.markdown,
						aupViolation: historyEvent.aupViolation || false,
						multiPrompts: historyEvent.multiPrompts,
					};
				}),
				clearFromHistory: assign((context, event) => {
					if (event.type !== 'retry prompt' && event.type !== 'interrogate') {
						return {};
					}
					return { responseHistory: { entries: [], positionFromEnd: 0 } };
				}),
				setLatestPrompt: actions.choose([
					{
						cond: 'is base generate',
						actions: assign((context) => ({
							latestPrompt: {
								userInput: context?.userInput || '',
								userADFInput: context?.userADFInput,
							},
							aupViolation: false,
						})),
					},
					/**
					 * TODO AIFOLLOWUP: ensure that this condition is still correct
					 * after promptLabel is removed.
					 * This condition is for preset prompts such as 'Brainstorm'
					 * which require additional user input.
					 * Also check to see if event.type !== 'interrogate' still
					 * makes sense down the line.
					 */
					{
						cond: 'has custom prompt',
						actions: assign((context, event) => ({
							latestPrompt: {
								...(event.type !== 'interrogate' && {
									presetTitle: context.configItem,
								}),
								userInput: context?.userInput || '',
								userADFInput: context?.userADFInput,
							},
						})),
					},
					/**
					 * Falls through to options which do not take additional
					 * user input and is not base generate, e.g.
					 * - Improve writing
					 * - Summarize
					 * Upon interrogation, there may also be user input.
					 * We preference showing user input over the preset title for
					 * non-partial presets.
					 */
					{
						actions: assign((context) =>
							context?.userInput
								? {
										latestPrompt: {
											userInput: context?.userInput || '',
											userADFInput: context?.userADFInput,
										},
									}
								: {
										latestPrompt: {
											presetTitle: context.configItem,
										},
									},
						),
					},
				]),
				clearFocus: () => {
					(document.activeElement as HTMLElement | null)?.blur();
				},
				fail: assign((context, event) => {
					if (
						event.type !== 'unknown fail' &&
						event.type !== 'token limit fail' &&
						event.type !== 'input too short fail' &&
						event.type !== 'hipaa content fail'
					) {
						return {};
					}

					return { failureReason: event.type, apiErrorInfo: event.errorInfo };
				}),
				endExperience: () => {
					options.endExperience();
				},
				updateContext: assign((_context, event) => {
					if (
						event.type === 'kickoff with command palette input' ||
						event.type === 'update context' ||
						event.type === 'enable discard'
					) {
						//TODO: multiPrompts experiment cleanup - editor_ai_-_multi_prompts
						return { ...event.context, multiPrompts: undefined };
					}
					if (event.type === 'disable discard') {
						//TODO: multiPrompts experiment cleanup - editor_ai_-_multi_prompts
						return { onceDiscard: undefined, multiPrompts: undefined };
					}
					return {};
				}),
				updateChannelId: assign((context, event) => {
					const { aiProvider, configItem } = context;

					if (!aiProvider.getChannelVariables || !configItem.agent) {
						return {
							channelId: undefined,
							previousAgentId: undefined,
						};
					}

					// if agent did not change, we don't need to update channelId preserving the same conversation history
					// as soon as agent changes, we need to recreate channelId to start a new conversation
					if (configItem.agent.id === context.previousAgentId) {
						return {};
					}

					return {
						channelId: buildChannelId(),
						previousAgentId: configItem.agent.id,
					};
				}),
				updateIdMap: assign((context, event) => {
					/**
					 * The idMap is not persisted between each new experience.
					 *
					 * Multiple calls to this are expected from config items:
					 * *selection -> markdown*
					 * - The selection is always sent for 'range' selectionType config items.
					 *
					 * *document -> markdown*
					 * - The document is always sent for 'empty' selectionType config items.
					 * - The document can be sent for 'range' selectionType config items.
					 */
					if (event.type === 'update idMap') {
						/**
						 * - Always update the idMap in state on the first call.
						 * - Always update the idMap in state if the selectionType is 'empty'
						 *   as these config items won't ever have a user selection.
						 */
						if (!context.idMap || event.context.selectionType === 'empty') {
							return { idMap: { ...event.context.idMap } };
						}
						/**
						 * If the selectionType is 'range' we only want to store an idMap
						 * for whatever the user has selected.
						 */
						if (event.context.selectionType === 'range') {
							/**
							 * - Always update the idMap in state for a user selection.
							 * - Always update the idMap in state if the user has selected the document.
							 * - Do not update the idMap if there have been multiple calls
							 *   to updateIdMap where one of those calls was for the whole document
							 *   and the other was for a user selection.
							 */
							if (event.context.idMap) {
								/**
								 * A selection cannot have more items than a document, so we can
								 * compare map lengths to determine whether to update or not.
								 *
								 * If the user has selected the whole document, the incoming idMap
								 * will have the same number of items as the idMap already in state.
								 * */
								const incomingIdMapIsUserSelection =
									event.context.idMap &&
									Object.keys(event.context.idMap).length <= Object.keys(context.idMap).length;

								if (incomingIdMapIsUserSelection) {
									return {
										idMap: { ...event.context.idMap },
									};
								}
							}
						}

						return {
							idMap: { ...context.idMap },
						};
					}
					return {};
				}),
				editPrompt: assign((_context, event) => {
					if (event.type !== 'edit prompt') {
						return {};
					}

					// from 'neither base generate nor custom prompt'
					const isBaseGenerate = _context.configItem.title === _context.baseGenerate?.title;
					const hasCustomPrompt = _context.configItem.promptLabel !== undefined;
					// if it's a preset prompt which doesn't need user input (like `Summarize writing`)
					// - we want to reset configItem
					// - we want to reset the user input
					const isPresetPrompt = !isBaseGenerate && !hasCustomPrompt;
					let configItem = _context.configItem;

					if (editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
						// if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
						// - we don't want to reset the configItem
						//   - (keep the configItem) so that we can restore the selected parent later in UserInputCommandPaletteWithLogic
						// - we want to reset the user input
						const isSubmenuSelected = Boolean(_context.parentPresetPromptLabel);

						// when we add default agent for knowledge source, it doesn't need additional user input because it's triggered from the user input
						// - we want to reset the configItem
						// - we don't want to reset the user input
						const isDefaultAgent = Boolean(_context.configItem.agent?.isDefault);

						const resetConfigItem = (isPresetPrompt || isDefaultAgent) && !isSubmenuSelected;
						const resetUserInput = (isPresetPrompt || isSubmenuSelected) && !isDefaultAgent;

						if (resetConfigItem && _context.baseGenerate) {
							// from dismissCustomPrompt
							configItem = _context.baseGenerate;
						}

						// If we detect a preset prompt, clear everything
						// otherwise preserve what we can.
						return {
							markdown: undefined,
							userInput: resetUserInput ? undefined : event.userInput,
							userADFInput: resetUserInput ? undefined : event.userADFInput,
							presetTitle: resetUserInput ? undefined : event.presetTitle,
							configItem,
							latestPromptResponse: undefined,
							editCount: _context.editCount + 1,
							idMap: undefined,
						};
					} else {
						// if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
						// we want to keep the configItem so that we can restore the selected parent later in UserInputCommandPaletteWithLogic
						if (!_context.parentPresetPromptLabel && isPresetPrompt && _context.baseGenerate) {
							// from dismissCustomPrompt
							configItem = _context.baseGenerate;
						}

						// If we detect a preset prompt, clear everything
						// otherwise preserve what we can.
						return {
							markdown: undefined,
							userInput: isPresetPrompt ? undefined : event.userInput,
							userADFInput: isPresetPrompt ? undefined : event.userADFInput,
							presetTitle: isPresetPrompt ? undefined : event.presetTitle,
							configItem,
							latestPromptResponse: undefined,
							editCount: _context.editCount + 1,
							idMap: undefined,
						};
					}
				}),
				incrementRetryPromptCount: assign((context, _event) => {
					return {
						retryPromptCount: context.retryPromptCount + 1,
					};
				}),
				updatePromptContextBasedOnTrigger: actions.choose([
					{
						actions: assign<AIExperienceMachineContext>((_context, event) => {
							if (event.type === 'interrogate') {
								return {
									promptTrigger: 'interrogate',
									latestPromptRetried: false,
								};
							}
							if (event.type === 'retry prompt') {
								return { latestPromptRetried: true };
							}
							if (event.type === 'kickoff with command palette input') {
								return {
									promptTrigger: 'generate',
									latestPromptRetried: false,
								};
							}
							return {};
						}),
					},
				]) as any,
			},
			delays: {
				RETRY_ALLOWED: ((context: AIExperienceMachineContext, event: AnyEventObject) => {
					// Convert Retry-After value from seconds to milliseconds
					return event.retryAfter * 1000;
					// Typed as any due to old xstate version not handling
					// the inference correctly
				}) as any,
			},
			guards: {
				/**
				 * TODO AIFOLLOWUP: promptLabel will be removed in favour of
				 * adding an attribute to the config item to determine
				 * whether a preset prompt supports additional user input
				 * Adjust the conditions in setLatestPrompt's `choose` accordingly
				 *
				 * *WithLogic use presetPromptLabel/parentPresetPromptLabel (Label for the selected partial prompt) to determine if there is a partial prompt
				 */
				['has custom prompt']: (context, event) => {
					// when we add default agent for knowledge source, it doesn't need additional user input because it's triggered from the user input
					const isDefaultAgent = Boolean(context.configItem.agent?.isDefault);
					if (
						isDefaultAgent &&
						editorExperiment('platform_editor_ai_command_palate_improvement', 'test')
					) {
						return false;
					}

					return context.configItem.promptLabel !== undefined;
				},
				['is base generate']: (context, event) => {
					// Doing configItem direct comparision fails in tests where we mutate or create custom
					// items.
					return context.configItem.title === context.baseGenerate?.title;
				},
				['neither base generate nor custom prompt']: (context, event) => {
					const isBaseGenerate = context.configItem.title === context.baseGenerate?.title;
					const hasCustomPrompt = context.configItem.promptLabel !== undefined;
					return !isBaseGenerate && !hasCustomPrompt;
				},
				['if command input is empty']: (context, event) => {
					return context.userInput?.trim?.()?.length === 0;
				},
			},
		},
	);

	return machine;
}

// Typed explicitly due to old xstate version not having
// type generation.
// Whenever adding a new state to the machine -- you will need to update this union
export type AIExperienceMachineState =
	| {
			['modal active state']: {
				experience:
					| {
							['loading state']:
								| 'loading'
								| 'api failed state'
								| 'rate limit active'
								| 'rate limit inactive';
					  }
					| { ['review state']: 'preview' | 'refinement' }
					| 'command palette state'
					| 'failed';
				discard: 'active' | 'inactive';
			};
	  }
	| 'started state';

type ModelInput = {
	/**
	 * This is the selection in markdown format, that will be sent to the AI
	 *
	 * note -- this will only be available for range selections
	 */
	selection?: string;
};

// Typed explicitly due to old xstate version not having
// type generation
// Whenever adding a new event to the machine -- you will need to update this union
export type AIExperienceMachineEvent =
	| {
			type: 'kickoff with command palette input';
			context: {
				userInput: string;
				userADFInput?: JSONDocNode;
				configItem: EditorPluginAIConfigItemMarkdown;
				parentPresetPromptLabel?: string;
			};
	  }
	| {
			type: 'stream';
			markdown: string;
			//TODO: AI Button experiment cleanup - platform_editor_ai_ai_button_block_elements
			fragment?: Fragment;
			//TODO: multiPrompts experiment cleanup - editor_ai_-_multi_prompts
			multiPrompts?: MultiPromptType[];
			loadingStatus?: string;
	  }
	| {
			type: 'complete';
			markdown: string;
			//TODO: AI Button experiment cleanup - platform_editor_ai_ai_button_block_elements
			fragment?: Fragment;
			cacheKey: string;
			modelInput: ModelInput;
			rovoActions?: ConvoAIResponseRovoAction[];
			inputOutputDiffRatio?: string;
	  }
	| {
			//TODO: multiPrompts experiment cleanup - editor_ai_-_multi_prompts
			type: 'handle additional prompts';
			multiPrompts: MultiPromptType[];
	  }
	| {
			type: 'AUP violation detected';
			cacheKey: string;
			modelInput: ModelInput;
			errorInfo?: {
				errorKey?: string;
				statusCode?: number;
				errorContent?: string;
				apiName?: string;
			};
	  }
	| {
			type: 'response too similar';
			cacheKey: string;
			modelInput: ModelInput;
			inputOutputDiffRatio?: string;
	  }
	| { type: 'insert failure' }
	| {
			type: 'unknown fail';
			failureReason: string;
			errorInfo: {
				errorKey?: string;
				statusCode?: number;
			};
	  }
	| { type: 'api retry' }
	| {
			type: 'api fail';
			errorInfo: {
				errorKey?: string;
				statusCode?: number;
				errorContent?: string;
				apiName?: string;
			};
	  }
	| {
			type: 'hipaa content fail';
			errorInfo: {
				errorKey?: string;
				statusCode?: number;
				errorContent?: string;
				apiName?: string;
			};
	  }
	| {
			type: 'interrogate';
			latestPromptResponse: string;
			userInput: string;
			userADFInput?: JSONDocNode;
	  }
	| {
			type: 'rate limited';
			retryAfter: number;
			errorInfo?: {
				errorKey?: string;
				statusCode?: number;
				errorContent?: string;
				apiName?: string;
			};
	  }
	| { type: 'kick off' }
	| {
			type: 'token limit fail';
			errorInfo: {
				errorKey?: string;
				statusCode?: number;
				errorContent?: string;
				apiName?: string;
			};
	  }
	| {
			type: 'input too short fail';
			errorInfo: {
				errorKey?: string;
				statusCode?: number;
			};
	  }
	| { type: 'same cache key' }
	// Nested user input events
	| { type: 'update positions'; positions: [start: number, end: number] }
	| { type: 'update user input'; input: string }
	| { type: 'update user input adf'; adf?: JSONDocNode }
	| {
			type: 'retry prompt';
			input: string | undefined;
			adf: JSONDocNode | undefined;
	  }
	| {
			type: 'edit prompt';
			userInput?: string;
			userADFInput?: JSONDocNode;
			presetTitle?: string;
	  }
	| {
			type: 'update context';
			context: {
				userInput: string;
				userADFInput?: JSONDocNode;
				configItem: EditorPluginAIConfigItemMarkdown;
			};
	  }
	| {
			type: 'update idMap';
			context: {
				idMap: IDMap;
				selectionType: 'empty' | 'range';
			};
	  }
	| { type: 'start refinement' }
	| { type: 'cancel refinement' }
	// discard flow
	| { type: 'enable discard'; context?: { onceDiscard?: () => unknown } }
	| { type: 'disable discard' }
	| { type: 'cancel' }
	| { type: 'escape' }
	| {
			type: 'storeContextStatistics';
			contextStatistics: ContextStatistics;
	  };
